import { useEffect, useState, useCallback } from 'react';
import { EntrenadoresService } from '../../../services/entrenadoresServices';
import theme from '../adminTheme';
import AdminSpinner from '../components/AdminSpinner';
import ConfirmModal from '../components/ConfirmModal';
import AdminPagination from '../components/AdminPagination';
import AdminModal from '../components/AdminModal';
import { useToast } from '../context/ToastContext';

const styles = {
  container: { padding: 24, maxWidth: 1200 },
  title: { fontSize: 28, fontWeight: 700 },
  subtitle: { color: '#666', marginBottom: 20 },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 16
  },

  searchContainer: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flex: 1,
    maxWidth: 400
  },

  searchInput: {
    flex: 1,
    padding: '10px 14px',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14
  },

  card: {
    background: '#fff',
    borderRadius: 12,
    padding: 16,
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)'
  },

  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: 12, borderBottom: '1px solid #e0e0e0', textAlign: 'left', fontWeight: 600 },
  td: { padding: 12, borderBottom: '1px solid #f0f0f0' },

  badge: (estado) => ({
    padding: '4px 12px',
    borderRadius: 14,
    fontSize: 12,
    fontWeight: 600,
    color:
      estado === 'activo' ? '#2e7d32' :
      estado === 'inactivo' ? '#ef6c00' :
      estado === 'finalizado' ? '#666' : '#333',
    background:
      estado === 'activo' ? '#e8f5e9' :
      estado === 'inactivo' ? '#fff3e0' :
      estado === 'finalizado' ? '#f5f5f5' : '#eee'
  }),

  error: {
    color: '#c62828',
    padding: '12px',
    background: '#ffebee',
    borderRadius: '8px',
    marginBottom: '16px'
  },

  success: {
    color: '#2e7d32',
    padding: '12px',
    background: '#e8f5e9',
    borderRadius: '8px',
    marginBottom: '16px'
  },

  loading: { textAlign: 'center', padding: '24px', color: '#666' },
  empty: { textAlign: 'center', padding: '24px', color: '#666' },

  btnPrimary: {
    padding: '8px 14px',
    background: '#1a4d8c',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },

  btnSecondary: {
    padding: '8px 14px',
    background: '#f4f6f8',
    color: '#111',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600,
    marginRight: 8
  },

  btnDanger: {
    padding: '8px 14px',
    background: '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 600
  },

  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 20,
    padding: '12px 0',
    borderTop: '1px solid #eee'
  },

  paginationInfo: { color: '#666', fontSize: 14 },
  paginationButtons: { display: 'flex', gap: 8 },

  btnPagination: {
    background: '#fff',
    color: '#1976d2',
    border: '1px solid #1976d2',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500
  },

  btnPaginationDisabled: {
    background: '#f5f5f5',
    color: '#999',
    border: '1px solid #ddd',
    padding: '8px 16px',
    borderRadius: 6,
    cursor: 'not-allowed',
    fontSize: 13
  },

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  },

  modal: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxWidth: 500,
    maxHeight: '90vh',
    overflow: 'auto'
  },

  modalTitle: { fontSize: 20, fontWeight: 600, marginBottom: 20 },

  formGroup: { marginBottom: 16 },
  label: { display: 'block', marginBottom: 6, fontWeight: 500, color: '#333' },

  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box'
  },

  select: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
    background: '#fff'
  },

  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: 6,
    fontSize: 14,
    boxSizing: 'border-box',
    minHeight: 80,
    resize: 'vertical'
  },

  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 24
  },

  actions: { display: 'flex', gap: 8 }
};

const EntrenadoresView = () => {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    entrenador_id: '',
    atleta_id: '',
    estado: 'activo',
    fecha_inicio: '',
    fecha_fin: '',
    observaciones: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { showToast } = useToast();

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchList = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await EntrenadoresService.listarAdmin(page, 10, search);
      setItems(data.items || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        total_pages: 1,
        has_next: false,
        has_prev: false
      });
    } catch (err) {
      console.error('Error al cargar relaciones:', err);
      setError(err.message || 'Error al cargar las relaciones entrenador-atleta');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleNextPage = () => {
    if (pagination.has_next) setPage((p) => p + 1);
  };

  const handlePrevPage = () => {
    if (pagination.has_prev) setPage((p) => p - 1);
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        entrenador_id: item.entrenador_id || '',
        atleta_id: item.atleta_id || '',
        estado: item.estado || 'activo',
        fecha_inicio: item.fecha_inicio || '',
        fecha_fin: item.fecha_fin || '',
        observaciones: item.observaciones || ''
      });
    } else {
      setEditingItem(null);
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        entrenador_id: '',
        atleta_id: '',
        estado: 'activo',
        fecha_inicio: today,
        fecha_fin: '',
        observaciones: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({
      entrenador_id: '',
      atleta_id: '',
      estado: 'activo',
      fecha_inicio: '',
      fecha_fin: '',
      observaciones: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      if (editingItem) {
        await EntrenadoresService.editar(editingItem.id, {
          estado: formData.estado,
          fecha_inicio: formData.fecha_inicio || undefined,
          fecha_fin: formData.fecha_fin || undefined,
          observaciones: formData.observaciones || undefined
        });
        setSuccess('Relación actualizada correctamente');
      } else {
        await EntrenadoresService.crear({
          entrenador_id: formData.entrenador_id.trim(),
          atleta_id: formData.atleta_id.trim(),
          estado: formData.estado,
          fecha_inicio: formData.fecha_inicio || undefined,
          fecha_fin: formData.fecha_fin || undefined,
          observaciones: formData.observaciones || undefined
        });
        setSuccess('Relación entrenador-atleta creada correctamente');
      }
      handleCloseModal();
      fetchList();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteId) return;
    setDeleting(true);
    try {
      await EntrenadoresService.eliminar(confirmDeleteId);
      setConfirmDeleteId(null);
      showToast('Relación eliminada correctamente', 'success');
      fetchList();
    } catch (err) {
      setError(err.message || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const nombreCompleto = (u) => {
    if (!u) return '-';
    const full = `${u.nombre || ''} ${u.apellido || ''}`.trim();
    return full || u.email || u.rut || '-';
  };

  if (loading && items.length === 0) {
    return (
      <div style={theme.container}>
        <h1 style={theme.title}>Entrenadores</h1>
        <p style={theme.subtitle}>Relaciones entrenador – atleta</p>
        <AdminSpinner label="Cargando..." />
      </div>
    );
  }

  return (
    <div style={theme.container}>
      <div style={theme.pageHeader}>
        <div>
          <h1 style={theme.title}>Entrenadores</h1>
          <p style={theme.subtitle}>Gestión de relaciones entrenador – atleta</p>
        </div>
        <form onSubmit={handleSearch} style={theme.searchContainer}>
          <input
            type="text"
            placeholder="Buscar por nombre, RUT o email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={theme.searchInput}
            aria-label="Buscar relaciones"
          />
          <button type="submit" style={theme.btnSecondary}>Buscar</button>
          {search && (
            <button type="button" style={theme.btnSecondary} onClick={handleClearSearch}>
              Limpiar
            </button>
          )}
        </form>
        <button style={theme.btnPrimary} onClick={() => handleOpenModal()} aria-label="Nueva relación">
          + Nueva relación
        </button>
      </div>

      {error && <div style={theme.error}><strong>Error:</strong> {error}</div>}
      {success && <div style={theme.success}>{success}</div>}

      <div style={theme.card}>
        {items.length === 0 ? (
          <p style={theme.empty}>
            {search
              ? `No hay resultados para "${search}"`
              : 'No hay relaciones entrenador-atleta registradas'}
          </p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  <th style={theme.th}>Entrenador</th>
                  <th style={theme.th}>RUT Ent.</th>
                  <th style={theme.th}>Atleta</th>
                  <th style={theme.th}>RUT Atl.</th>
                  <th style={theme.th}>Estado</th>
                  <th style={theme.th}>Inicio</th>
                  <th style={theme.th}>Fin</th>
                  <th style={theme.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id}>
                    <td style={theme.td}>{nombreCompleto(r.entrenador)}</td>
                    <td style={theme.td}>{r.entrenador_id || '-'}</td>
                    <td style={theme.td}>{nombreCompleto(r.atleta)}</td>
                    <td style={theme.td}>{r.atleta_id || '-'}</td>
                    <td style={theme.td}>
                      <span style={{ padding: '4px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600, color: r.estado === 'activo' ? '#2e7d32' : r.estado === 'inactivo' ? '#ef6c00' : '#666', background: r.estado === 'activo' ? '#e8f5e9' : r.estado === 'inactivo' ? '#fff3e0' : '#f5f5f5' }}>
                        {r.estado || 'activo'}
                      </span>
                      {r.es_auto_entrenamiento && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#666' }}>
                          (auto)
                        </span>
                      )}
                    </td>
                    <td style={theme.td}>
                      {r.fecha_inicio ? new Date(r.fecha_inicio).toLocaleDateString() : '-'}
                    </td>
                    <td style={theme.td}>
                      {r.fecha_fin ? new Date(r.fecha_fin).toLocaleDateString() : '-'}
                    </td>
                    <td style={theme.td}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button style={theme.btnSecondary} onClick={() => handleOpenModal(r)} aria-label="Editar">Editar</button>
                        <button style={theme.btnDanger} onClick={() => setConfirmDeleteId(r.id)} aria-label="Eliminar">Eliminar</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <AdminPagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              total={pagination.total}
              pageSize={10}
              hasPrev={pagination.has_prev}
              hasNext={pagination.has_next}
              onPrev={handlePrevPage}
              onNext={handleNextPage}
              label="relaciones"
            />
          </>
        )}
      </div>

      <ConfirmModal
        open={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar relación"
        message="¿Eliminar esta relación entrenador-atleta?"
        confirmLabel="Eliminar"
        cancelLabel="Cancelar"
        variant="danger"
        loading={deleting}
      />

      {showModal && (
        <AdminModal open onClose={handleCloseModal} title={editingItem ? 'Editar relación' : 'Nueva relación entrenador – atleta'} width={500}>
          <form onSubmit={handleSubmit}>
              {!editingItem && (
                <>
                  <div style={theme.formGroup}>
                    <label style={theme.label}>RUT del entrenador *</label>
                    <input
                      type="text"
                      name="entrenador_id"
                      value={formData.entrenador_id}
                      onChange={handleInputChange}
                      placeholder="Ej: 12345678-9"
                      style={theme.input}
                      required
                    />
                  </div>
                  <div style={theme.formGroup}>
                    <label style={theme.label}>RUT del atleta *</label>
                    <input
                      type="text"
                      name="atleta_id"
                      value={formData.atleta_id}
                      onChange={handleInputChange}
                      placeholder="Ej: 98765432-1"
                      style={theme.input}
                      required
                    />
                  </div>
                </>
              )}

              {editingItem && (
                <p style={{ color: '#666', marginBottom: 16 }}>
                  {nombreCompleto(editingItem.entrenador)} → {nombreCompleto(editingItem.atleta)}
                </p>
              )}

              <div style={theme.formGroup}>
                <label style={theme.label}>Estado</label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleInputChange}
                  style={theme.select}
                >
                  <option value="activo">Activo</option>
                  <option value="inactivo">Inactivo</option>
                  <option value="finalizado">Finalizado</option>
                </select>
              </div>

              <div style={theme.formGroup}>
                <label style={theme.label}>Fecha inicio</label>
                <input
                  type="date"
                  name="fecha_inicio"
                  value={formData.fecha_inicio}
                  onChange={handleInputChange}
                  style={theme.input}
                />
              </div>

              <div style={theme.formGroup}>
                <label style={theme.label}>Fecha fin</label>
                <input
                  type="date"
                  name="fecha_fin"
                  value={formData.fecha_fin}
                  onChange={handleInputChange}
                  style={theme.input}
                />
              </div>

              <div style={theme.formGroup}>
                <label style={theme.label}>Observaciones</label>
                <textarea
                  name="observaciones"
                  value={formData.observaciones}
                  onChange={handleInputChange}
                  style={theme.textarea}
                  placeholder="Opcional"
                />
              </div>

              <div style={theme.modalActions}>
                <button type="button" style={theme.btnSecondary} onClick={handleCloseModal} disabled={submitting}>Cancelar</button>
                <button type="submit" style={theme.btnPrimary} disabled={submitting}>
                  {submitting ? 'Guardando...' : editingItem ? 'Actualizar' : 'Crear'}
                </button>
              </div>
          </form>
        </AdminModal>
      )}
    </div>
  );
};

export default EntrenadoresView;
